/**
 * HtmlTreeViewer Component
 *
 * Collapsible HTML/XML tree viewer like Chrome DevTools Elements panel.
 * Parses an HTML string with data-abox-id markers and renders a nested
 * expandable/collapsible tree. Supports bidirectional highlighting with
 * the ABox table.
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';

interface HtmlTreeViewerProps {
  html: string;
  /** Currently highlighted ABox element ID (scrolls to and highlights matching node). */
  highlightElementId?: number | null;
  /** Called when user clicks a node with data-abox-id. */
  onElementClick?: (elementId: number) => void;
  /** Label to show in toolbar (e.g. "Step 3 HTML"). */
  label?: string;
  /** Called when "Fetch Live" button is clicked. */
  onFetchLive?: () => void;
  /** Whether fetching live HTML is currently in progress. */
  isLiveLoading?: boolean;
}

/** Parsed node for rendering. */
interface TreeNode {
  type: 'element' | 'text' | 'comment';
  tagName?: string;
  attributes?: Record<string, string>;
  children?: TreeNode[];
  textContent?: string;
  aboxId?: number;
  selfClosing?: boolean;
}

const SELF_CLOSING_TAGS = new Set([
  'area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input',
  'link', 'meta', 'param', 'source', 'track', 'wbr',
]);

/** Parse HTML string into a tree structure. */
function parseHtmlToTree(html: string): TreeNode[] {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  return domToTree(doc.documentElement);
}

function domToTree(node: Node): TreeNode[] {
  const result: TreeNode[] = [];

  for (let i = 0; i < node.childNodes.length; i++) {
    const child = node.childNodes[i];

    if (child.nodeType === Node.ELEMENT_NODE) {
      const el = child as Element;
      const tagName = el.tagName.toLowerCase();

      const attributes: Record<string, string> = {};
      for (let j = 0; j < el.attributes.length; j++) {
        const attr = el.attributes[j];
        attributes[attr.name] = attr.value;
      }

      const aboxIdStr = attributes['data-abox-id'];
      const aboxId = aboxIdStr ? parseInt(aboxIdStr, 10) : undefined;

      const treeNode: TreeNode = {
        type: 'element',
        tagName,
        attributes,
        children: domToTree(el),
        aboxId: aboxId !== undefined && !isNaN(aboxId) ? aboxId : undefined,
        selfClosing: SELF_CLOSING_TAGS.has(tagName) && el.childNodes.length === 0,
      };
      result.push(treeNode);
    } else if (child.nodeType === Node.TEXT_NODE) {
      const text = child.textContent || '';
      if (text.trim()) {
        result.push({ type: 'text', textContent: text.trim() });
      }
    } else if (child.nodeType === Node.COMMENT_NODE) {
      result.push({ type: 'comment', textContent: child.textContent || '' });
    }
  }

  return result;
}

/** Render attributes as colored spans. */
function renderAttributes(attrs: Record<string, string>): React.ReactNode[] {
  return Object.entries(attrs).map(([name, value]) => (
    <span key={name}>
      {' '}
      <span className="text-orange-600">{name}</span>
      <span className="text-gray-500">=</span>
      <span className="text-blue-600">"{value.length > 60 ? value.slice(0, 60) + '...' : value}"</span>
    </span>
  ));
}

/** Check if a node or any descendant contains the target abox ID. */
function subtreeContainsAboxId(node: TreeNode, aboxId: number): boolean {
  if (node.aboxId === aboxId) return true;
  if (node.children) {
    return node.children.some((c) => subtreeContainsAboxId(c, aboxId));
  }
  return false;
}

/** Single tree node component. */
const TreeNodeView: React.FC<{
  node: TreeNode;
  depth: number;
  highlightAboxId?: number | null;
  onElementClick?: (elementId: number) => void;
  highlightRef?: React.RefObject<HTMLDivElement | null>;
}> = ({ node, depth, highlightAboxId, onElementClick, highlightRef }) => {
  const [expanded, setExpanded] = useState(depth < 2);
  const isHighlighted = node.aboxId !== undefined && node.aboxId === highlightAboxId;
  const hasChildren = node.children && node.children.length > 0;
  const indent = depth * 16;

  // Auto-expand path to highlighted node (expand if this node or any descendant is highlighted)
  useEffect(() => {
    if (highlightAboxId != null && hasChildren && subtreeContainsAboxId(node, highlightAboxId)) {
      setExpanded(true);
    }
  }, [highlightAboxId, node, hasChildren]);

  if (node.type === 'text') {
    const text = node.textContent || '';
    const truncated = text.length > 120 ? text.slice(0, 120) + '...' : text;
    return (
      <div
        className="font-mono text-xs leading-5 text-gray-700"
        style={{ paddingLeft: indent }}
      >
        {truncated}
      </div>
    );
  }

  if (node.type === 'comment') {
    return (
      <div
        className="font-mono text-xs leading-5 text-gray-400 italic"
        style={{ paddingLeft: indent }}
      >
        {'<!-- '}{node.textContent}{' -->'}
      </div>
    );
  }

  const attrs = node.attributes || {};
  const isAboxElement = node.aboxId !== undefined;

  return (
    <div>
      <div
        ref={isHighlighted ? highlightRef : undefined}
        className={`font-mono text-xs leading-5 select-text ${
          isHighlighted
            ? 'bg-blue-100 border-l-2 border-blue-500'
            : isAboxElement
              ? 'hover:bg-yellow-50 cursor-pointer'
              : 'hover:bg-gray-50'
        }`}
        style={{ paddingLeft: indent }}
        onClick={(e) => {
          if (isAboxElement && onElementClick) {
            e.stopPropagation();
            onElementClick(node.aboxId!);
          }
        }}
      >
        {/* Expand/collapse toggle */}
        {hasChildren && !node.selfClosing ? (
          <span
            className="inline-block w-3 text-center text-gray-400 cursor-pointer hover:text-gray-600 select-none"
            onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
          >
            {expanded ? '\u25BE' : '\u25B8'}
          </span>
        ) : (
          <span className="inline-block w-3" />
        )}

        {/* Opening tag */}
        <span className="text-gray-500">&lt;</span>
        <span className={`${isAboxElement ? 'text-purple-700 font-semibold' : 'text-pink-600'}`}>
          {node.tagName}
        </span>
        {renderAttributes(attrs)}
        {node.selfClosing ? (
          <span className="text-gray-500"> /&gt;</span>
        ) : (
          <span className="text-gray-500">&gt;</span>
        )}

        {/* ABox indicator */}
        {isAboxElement && (
          <span className="ml-1 px-1 py-0 rounded text-[10px] bg-purple-100 text-purple-600 font-medium">
            abox:{node.aboxId}
          </span>
        )}

        {/* Inline closing for simple text-only children when collapsed */}
        {!expanded && hasChildren && !node.selfClosing && (
          <>
            <span className="text-gray-400">...</span>
            <span className="text-gray-500">&lt;/</span>
            <span className={isAboxElement ? 'text-purple-700' : 'text-pink-600'}>
              {node.tagName}
            </span>
            <span className="text-gray-500">&gt;</span>
          </>
        )}
      </div>

      {/* Children */}
      {expanded && hasChildren && !node.selfClosing && (
        <>
          {node.children!.map((child, i) => (
            <TreeNodeView
              key={i}
              node={child}
              depth={depth + 1}
              highlightAboxId={highlightAboxId}
              onElementClick={onElementClick}
              highlightRef={highlightRef}
            />
          ))}
          {/* Closing tag */}
          <div
            className="font-mono text-xs leading-5 text-gray-500"
            style={{ paddingLeft: indent + 12 }}
          >
            &lt;/<span className={isAboxElement ? 'text-purple-700' : 'text-pink-600'}>
              {node.tagName}
            </span>&gt;
          </div>
        </>
      )}
    </div>
  );
};

export const HtmlTreeViewer: React.FC<HtmlTreeViewerProps> = ({
  html,
  highlightElementId,
  onElementClick,
  label,
  onFetchLive,
  isLiveLoading,
}) => {
  const tree = useMemo(() => parseHtmlToTree(html), [html]);
  const highlightRef = useRef<HTMLDivElement | null>(null);

  // Auto-scroll to highlighted element
  useEffect(() => {
    if (highlightElementId !== null && highlightElementId !== undefined && highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightElementId]);

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Toolbar */}
      {(label || onFetchLive) && (
        <div className="flex items-center justify-between px-2 py-1 border-b border-gray-200 flex-shrink-0 bg-gray-50">
          {label && (
            <span className="text-xs font-medium text-gray-600">{label}</span>
          )}
          {onFetchLive && (
            <button
              onClick={onFetchLive}
              disabled={isLiveLoading}
              className="px-2 py-0.5 text-xs font-medium text-blue-600 bg-white border border-blue-300 rounded hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLiveLoading ? 'Fetching...' : 'Fetch Live'}
            </button>
          )}
        </div>
      )}
      {/* Tree */}
      <div className="flex-1 min-h-0 overflow-auto p-2">
        {tree.map((node, i) => (
          <TreeNodeView
            key={i}
            node={node}
            depth={0}
            highlightAboxId={highlightElementId}
            onElementClick={onElementClick}
            highlightRef={highlightRef}
          />
        ))}
      </div>
    </div>
  );
};
